import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Search, Filter, MoreHorizontal, Eye, Download, Building2, RefreshCw, Trash2, Plus, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { assignLoan, bulkUpdateLoans, deleteLoan, exportLoans, getAgents, getEmployees, getLoans, updateLoan } from '../../lib/api';
import { DeleteModal } from '../../components/ui/ActionModals';
import ExportDialog from '../../components/common/ExportDialog';
import { resolveLoanRoleBasePath } from '../../lib/loanApplicationFlow';

const statusColors = {
  'Under Processing': 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  'Resubmission Required': 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
  Approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  Disbursed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  Rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  Hold: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
};

const loanTypeDisplay = (value) => {
  const map = {
    home: 'Home Loan',
    personal: 'Personal Loan',
    business: 'Business Loan',
    car: 'Car Loan',
    education: 'Education Loan',
    other: 'Other',
  };
  return map[value] || value;
};

const loanStatusOptions = ['Under Processing', 'Approved', 'Disbursed', 'Rejected', 'Hold'];
const filterStatusOptions = ['Under Processing', 'Resubmission Required', 'Approved', 'Disbursed', 'Rejected', 'Hold'];

const buildPaginationItems = (page, totalPages) => {
  const pages = [];
  for (let current = 1; current <= totalPages; current += 1) {
    if (current === 1 || current === totalPages || Math.abs(current - page) <= 1) {
      pages.push(current);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
};

function TablePagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const items = buildPaginationItems(page, totalPages);
  return (
    <Pagination className="justify-end">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page > 1) onPageChange(page - 1);
            }}
            className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
        {items.map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {item === '...' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href="#"
                isActive={item === page}
                onClick={(event) => {
                  event.preventDefault();
                  onPageChange(item);
                }}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page < totalPages) onPageChange(page + 1);
            }}
            className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

const mapApiLoan = (loan) => ({
  id: loan.id,
  applicantName: loan.applicant_name,
  loanType: loanTypeDisplay(loan.loan_type),
  bank: loan.bank_name || '—',
  amount: Number(loan.loan_amount) || 0,
  interestRate: Number(loan.interest_rate) || 0,
  tenure: Number(loan.tenure_months) || 0,
  emi: Number(loan.emi) || 0,
  stage: loan.stage || 'processing',
  status: loan.status || 'Under Processing',
  remarks: loan.remarks || '',
  agentName: loan.agent_name || '—',
  assignedEmployeeName: loan.assigned_employee_name || '—',
  assignedToName: loan.assigned_to_name || loan.assigned_to || loan.assigned_employee_name || loan.agent_name || '—',
  assignedEmployeeId: loan.assigned_employee_id || '',
  createdByName: loan.created_by_name || '—',
  api: loan,
});

const LoansTable = ({ filter = 'all', title, description }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(filter);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [modal, setModal] = useState(null); // delete|status|assign
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [statusForm, setStatusForm] = useState({ status: '', stage: '', remarks: '' });
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const canAssign = user?.role === 'super_admin' || user?.role === 'wl_admin';
  const canCrud = user?.role === 'super_admin' || user?.role === 'wl_admin' || user?.role === 'employee';
  const basePath = resolveLoanRoleBasePath(location.pathname);
  const assignableEmployees = useMemo(() => {
    if (user?.role === 'super_admin') {
      return employees.filter((e) => !e.white_label_id);
    }
    return employees;
  }, [employees, user?.role]);
  const [selectedLoanIds, setSelectedLoanIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkAssignEmployeeId, setBulkAssignEmployeeId] = useState('');
  const [bulkAssignAgentId, setBulkAssignAgentId] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [allAgents, setAllAgents] = useState([]);
  const assignableAgents = useMemo(() => {
    if (user?.role === 'agent') return [];
    if (allAgents.length === 0) return [];
    if (user?.role === 'super_admin') {
      if (bulkAssignEmployeeId === 'Admin') {
        return allAgents.filter((a) => a.created_by_id === null || a.created_by_id === undefined);
      }
      if (bulkAssignEmployeeId) {
        return allAgents.filter((a) => a.created_by_id === bulkAssignEmployeeId);
      }
      return allAgents.filter((a) => a.created_by_id === user?.id);
    }
    return allAgents.filter((a) => a.created_by_id === user?.id);
  }, [allAgents, bulkAssignEmployeeId, user?.id, user?.role]);

  const openModal = (loan, type) => {
    setSelectedLoan(loan);
    setModal(type);
    if (type === 'status') {
      setStatusForm({
        status: loan?.status || 'Under Processing',
        stage: loan?.stage || 'processing',
        remarks: loan?.remarks || '',
      });
    }
    if (type === 'assign') {
      setAssignEmployeeId(loan?.assignedEmployeeId || '');
    }
  };
  const closeModal = () => {
    setModal(null);
    setSelectedLoan(null);
  };

  const openDetailsPage = (loanId) => {
    const role = user?.role;
    const base =
      role === 'wl_admin'
        ? '/whitelabel/loans'
        : role === 'employee'
          ? '/employee/loans'
          : '/admin/loans';
    navigate(`${base}/${loanId}`);
  };

  const fetchEmployees = useCallback(async () => {
    if (!token || !canAssign) return;
    try {
      const resp = await getEmployees(token);
      const items = resp?.data?.items ?? [];
      setEmployees(items);
    } catch {
      setEmployees([]);
    }
  }, [token, canAssign]);

  const fetchLoans = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const statusParam = statusFilter !== 'all' ? statusFilter : (filter !== 'all' ? filter : undefined);
      const resp = await getLoans(token, {
        status: statusParam,
        q: searchTerm.trim() || undefined,
        page,
        pageSize: 10,
      });
      const items = resp?.data?.items ?? [];
      setRows(items.map(mapApiLoan));
      const meta = resp?.data?.pagination || {};
      setPagination({
        page: Number(meta.page || page),
        pageSize: Number(meta.page_size || 10),
        total: Number(meta.total || items.length || 0),
        totalPages: Number(meta.total_pages || 1),
      });
    } catch (err) {
      setRows([]);
      setPagination({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
      });
      toast.error(err?.message || 'Failed to fetch loans');
    } finally {
      setLoading(false);
    }
  }, [token, filter, page, searchTerm, statusFilter]);

  useEffect(() => {
    fetchLoans();
    fetchEmployees();
  }, [fetchLoans, fetchEmployees]);

  const fetchAllAgents = useCallback(async () => {
    if (!token || user?.role === 'agent') return;
    try {
      const resp = await getAgents(token);
      const items = resp?.data?.items ?? [];
      setAllAgents(items);
    } catch {
      setAllAgents([]);
    }
  }, [token, user?.role]);

  useEffect(() => {
    fetchAllAgents();
  }, [fetchAllAgents]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, filter]);

  useEffect(() => {
    setSelectedLoanIds((prev) => prev.filter((id) => rows.some((row) => row.id === id)));
  }, [rows]);

  const toggleLoanSelection = (loanId, checked) => {
    setSelectedLoanIds((prev) => {
      const exists = prev.includes(loanId);
      if (checked === undefined) {
        if (exists) {
          return prev.filter((id) => id !== loanId);
        }
        return [...prev, loanId];
      }
      if (checked) {
        return exists ? prev : [...prev, loanId];
      }
      return exists ? prev.filter((id) => id !== loanId) : prev;
    });
  };

  const clearSelection = () => {
    setSelectedLoanIds([]);
    setBulkStatus('');
    setBulkAssignEmployeeId('');
    setBulkAssignAgentId('');
  };

  const handleAssignChange = (value) => {
    setBulkAssignAgentId('');
    setBulkAssignEmployeeId(value === '_none' ? '' : value);
  };

  const applyBulkLoanUpdate = async () => {
    const hasBulkStatus = Boolean(bulkStatus);
    const hasEmployeeAssignment = Boolean(bulkAssignEmployeeId && bulkAssignEmployeeId !== 'Admin');
    const hasAgentAssignment = Boolean(bulkAssignAgentId);
    if ((!hasBulkStatus && !hasEmployeeAssignment && !hasAgentAssignment) || !token || selectedLoanIds.length === 0) return;
    setBulkSaving(true);
    try {
      const payload = {
        loan_ids: selectedLoanIds,
      };
      if (hasBulkStatus) {
        payload.status = bulkStatus;
      }
      if (bulkAssignAgentId) {
        payload.assigned_to = bulkAssignAgentId;
      } else if (hasEmployeeAssignment) {
        payload.assigned_to = bulkAssignEmployeeId;
      }
      if (hasEmployeeAssignment) payload.employee_id = bulkAssignEmployeeId;
      const resp = await bulkUpdateLoans(token, payload);
      toast.success(resp?.message || `${resp?.updated_count ?? 0} loans updated`);
      clearSelection();
      fetchLoans();
    } catch (err) {
      toast.error(err?.message || 'Failed to update loans');
    } finally {
      setBulkSaving(false);
    }
  };

  const filteredRows = rows;

  const allRowsSelected = filteredRows.length > 0 && filteredRows.every((loan) => selectedLoanIds.includes(loan.id));
  const handleSelectAllChange = (checked) => {
    if (checked) {
      setSelectedLoanIds(filteredRows.map((loan) => loan.id));
      return;
    }
    clearSelection();
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

  const onUpdateStatus = async () => {
    if (!selectedLoan?.id) return;
    try {
      await updateLoan(token, selectedLoan.id, {
        status: statusForm.status,
        stage: statusForm.stage,
        remarks: statusForm.remarks || null,
      });
      toast.success('Loan updated');
      closeModal();
      fetchLoans();
    } catch (err) {
      toast.error(err?.message || 'Failed to update loan');
    }
  };

  const onAssign = async () => {
    if (!selectedLoan?.id || !assignEmployeeId) return;
    try {
      await assignLoan(token, selectedLoan.id, assignEmployeeId);
      toast.success('Loan assigned');
      closeModal();
      fetchLoans();
    } catch (err) {
      toast.error(err?.message || 'Failed to assign loan');
    }
  };

  const handleExport = async ({ format, startDate, endDate }) => {
    if (!token) return;
    setExporting(true);
    try {
      const statusParam = statusFilter !== 'all' ? statusFilter : (filter !== 'all' ? filter : undefined);
      await exportLoans(token, {
        format,
        status: statusParam,
        q: searchTerm.trim() || undefined,
        startDate,
        endDate,
      });
      toast.success(`Loan export downloaded (${format.toUpperCase()})`);
      setShowExportDialog(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to export loans');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="loans-management">
      <DeleteModal
        open={modal === 'delete'}
        onClose={closeModal}
        title={selectedLoan?.id}
        description={`Are you sure you want to delete loan file "${selectedLoan?.id}" for ${selectedLoan?.applicantName}?`}
        onConfirm={async () => {
          if (!selectedLoan?.id) return;
          await deleteLoan(token, selectedLoan.id);
          await fetchLoans();
        }}
      />
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        title="Export Loans"
        description="Select format and date range before exporting loan data."
        onSubmit={handleExport}
        loading={exporting}
      />

      <Dialog open={modal === 'status'} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Update Loan Status</DialogTitle>
            <DialogDescription>{selectedLoan?.id} — {selectedLoan?.applicantName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusForm.status} onValueChange={(v) => setStatusForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {loanStatusOptions.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                For document corrections, open the loan details and reject specific documents.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Stage</Label>
              <Input value={statusForm.stage} onChange={(e) => setStatusForm((p) => ({ ...p, stage: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input value={statusForm.remarks} onChange={(e) => setStatusForm((p) => ({ ...p, remarks: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button onClick={onUpdateStatus} className="bg-purple-600 hover:bg-purple-700 text-white">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === 'assign'} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Assign Loan</DialogTitle>
            <DialogDescription>{selectedLoan?.id} — {selectedLoan?.applicantName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Employee</Label>
            <Select value={assignEmployeeId || '_none'} onValueChange={(v) => setAssignEmployeeId(v === '_none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Select employee</SelectItem>
                {assignableEmployees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button onClick={onAssign} className="bg-blue-600 hover:bg-blue-700 text-white" disabled={!assignEmployeeId}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {title || 'Loan / File Login'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {description || 'Track and manage all loan applications'}
          </p>
        </div>
        <div className="flex gap-2">
          {canCrud ? (
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate(`${basePath}/apply/products`)}>
              <Plus className="h-4 w-4 mr-2" />Add Loan
            </Button>
          ) : null}
          <Button variant="outline" data-testid="export-loans-btn" onClick={() => setShowExportDialog(true)}>
            <Download className="h-4 w-4 mr-2" />Export
          </Button>
        </div>
      </div>

      <Card className="border border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by loan ID or applicant name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-loans-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="loans-status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {filterStatusOptions.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {selectedLoanIds.length > 0 && (
        <Card className="border-2 border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-950/10 shadow-sm overflow-hidden">
          <CardContent className="p-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                <div className="h-8 w-8 rounded-full border-2 border-background bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>

              </div>
              <div>
                <p className="text-sm font-bold text-foreground">
                  {selectedLoanIds.length} Loans Selected
                </p>
                <p className="text-[11px] text-muted-foreground leading-none">
                  Batch update status or assignment
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Select value={bulkStatus || undefined} onValueChange={(v) => setBulkStatus(v)}>
                  <SelectTrigger className="w-[160px] h-9 bg-background border-emerald-500/20" disabled={bulkSaving}>
                    <SelectValue placeholder="Update Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {loanStatusOptions.map((status) => (
                      <SelectItem key={status} value={status}>
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${statusColors[status].split(' ')[0]}`} />
                          {status}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {user?.role === 'super_admin' && assignableEmployees.length > 0 && (
                  <Select value={bulkAssignEmployeeId || '_none'} onValueChange={handleAssignChange}>
                    <SelectTrigger className="w-[180px] h-9 bg-background border-emerald-500/20" disabled={bulkSaving}>
                      <SelectValue placeholder="By Employee" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">All (my agents)</SelectItem>
                        {assignableEmployees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          <div className="flex items-center gap-2">
                            <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
                            {employee.full_name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Select value={bulkAssignAgentId || '_none'} onValueChange={(v) => setBulkAssignAgentId(v === '_none' ? '' : v)}>
                  <SelectTrigger className="w-[180px] h-9 bg-background border-emerald-500/20" disabled={bulkSaving}>
                    <SelectValue placeholder="Assign Agent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No agent</SelectItem>
                    {assignableAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        <div className="flex items-center gap-2">
                          <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
                          {agent.full_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  onClick={applyBulkLoanUpdate}
                  disabled={bulkSaving || (!bulkStatus && !(bulkAssignEmployeeId && bulkAssignEmployeeId !== 'Admin') && !bulkAssignAgentId)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-5 font-semibold"
                >
                  {bulkSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Apply Changes'
                  )}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  disabled={bulkSaving}
                  className="text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 h-9"
                >
                  Close
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allRowsSelected}
                      onCheckedChange={(checked) => handleSelectAllChange(Boolean(checked))}
                      aria-label="Select all loans"
                    />
                  </TableHead>
                  <TableHead>Loan ID</TableHead>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Loan Type</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((loan) => (
                  <TableRow key={loan.id} data-testid={`loan-row-${loan.id}`} className="hover:bg-muted/30 transition-colors">
                    <TableCell className="w-12">
                      <Checkbox
                        checked={selectedLoanIds.includes(loan.id)}
                        onCheckedChange={(checked) => {
                          toggleLoanSelection(loan.id, Boolean(checked));
                        }}
                        aria-label={`Select loan ${loan.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">{loan.id}</TableCell>
                    <TableCell className="font-medium">{loan.applicantName}</TableCell>
                    <TableCell>{loan.loanType}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />{loan.bank}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{formatCurrency(loan.amount)}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[loan.status]}>{loan.status}</Badge>
                    </TableCell>
                    <TableCell>{loan.assignedToName}</TableCell>
                    <TableCell>{loan.createdByName}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`loan-actions-${loan.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => openDetailsPage(loan.id)} className="gap-2 cursor-pointer">
                            <Eye className="h-4 w-4 text-blue-500" /><span>View Details</span>
                          </DropdownMenuItem>
                          {canCrud && (
                            <DropdownMenuItem onClick={() => openModal(loan, 'status')} className="gap-2 cursor-pointer">
                              <RefreshCw className="h-4 w-4 text-purple-500" /><span>Update Status</span>
                            </DropdownMenuItem>
                          )}
                          {canAssign && (
                            <DropdownMenuItem onClick={() => openModal(loan, 'assign')} className="gap-2 cursor-pointer">
                              <UserPlus className="h-4 w-4 text-blue-500" /><span>Assign Employee</span>
                            </DropdownMenuItem>
                          )}
                          {canCrud && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => openModal(loan, 'delete')}
                                className="gap-2 cursor-pointer text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="h-4 w-4" /><span>Delete</span>
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {(loading || filteredRows.length === 0) && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{loading ? 'Loading loans...' : 'No loans found matching your criteria.'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Showing page {pagination.page} of {pagination.totalPages} · {pagination.total} loans
        </p>
        <TablePagination page={page} totalPages={pagination.totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

export const AllLoans = () => <LoansTable filter="all" />;
export const ProcessingLoans = () => <LoansTable filter="Under Processing" title="Under Processing" description="Loans currently being processed" />;
export const ApprovedLoans = () => <LoansTable filter="Approved" title="Approved Loans" description="Loans that have been approved" />;
export const DisbursedLoans = () => <LoansTable filter="Disbursed" title="Disbursed Loans" description="Loans that have been disbursed" />;
export const RejectedLoans = () => <LoansTable filter="Rejected" title="Rejected Loans" description="Loans that have been rejected" />;
export const HoldLoans = () => <LoansTable filter="Hold" title="Hold Cases" description="Loans on hold for review" />;

export default AllLoans;
